import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { apiService } from "../../../services/api";
import { PerfilPermissao, ModuloSistema, AcaoSistema } from '../../../types/admin';
import { modalUtils } from '../../../utils/modalUtils';
import { ModalDetalhesPerfil } from './components/ModalDetalhesPerfil';
import { ModalEditorPerfil } from './components/ModalEditorPerfil';

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: PerfilPermissao) => React.ReactNode;
  sorter?: (a: PerfilPermissao, b: PerfilPermissao) => number;
  key?: string;
}

export const GestaoPerfis = () => {
  const dispatch = useDispatch();
  const [perfis, setPerfis] = useState<PerfilPermissao[]>([]);
  const [filteredPerfis, setFilteredPerfis] = useState<PerfilPermissao[]>([]);
  const [modulos, setModulos] = useState<ModuloSistema[]>([]);
  const [acoes, setAcoes] = useState<AcaoSistema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Estados para modais
  const [perfilDetalhes, setPerfilDetalhes] = useState<PerfilPermissao | null>(null);
  const [perfilEditor, setPerfilEditor] = useState<PerfilPermissao | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Função para buscar perfis
  const fetchPerfis = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPerfisPermissao();

      // Adiciona a propriedade key para cada perfil (necessária para o Ant Design Table)
      const perfisComKey = response.data.map((perfil: PerfilPermissao) => ({
        ...perfil,
        key: perfil.id.toString()
      }));

      setPerfis(perfisComKey);
      setFilteredPerfis(perfisComKey);
      setError(null);
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
      setError('Erro ao carregar os perfis. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar módulos e ações
  const fetchModulosEAcoes = async () => {
    try {
      const [modulosResponse, acoesResponse] = await Promise.all([
        apiService.getModulosSistema(),
        apiService.getAcoesSistema()
      ]);
      setModulos(modulosResponse.data);
      setAcoes(acoesResponse.data);
    } catch (error) {
      console.error('Erro ao buscar módulos e ações:', error);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    Promise.all([fetchPerfis(), fetchModulosEAcoes()]);
  }, []);

  // Efeito para filtrar perfis
  useEffect(() => {
    let filtered = perfis;

    // Filtro por texto de busca
    if (searchText.trim()) {
      const searchTerms = searchText.toLowerCase().trim().split(' ');
      filtered = filtered.filter(perfil => {
        const searchableFields = [
          perfil.nome,
          perfil.codigo,
          perfil.descricao,
          perfil.ativo ? 'ativo' : 'inativo',
          perfil.is_padrao ? 'padrão' : 'personalizado'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });
    }

    // Filtro por status
    if (statusFilter !== 'all') {
      if (statusFilter === 'ativo') {
        filtered = filtered.filter(perfil => perfil.ativo);
      } else if (statusFilter === 'inativo') {
        filtered = filtered.filter(perfil => !perfil.ativo);
      } else if (statusFilter === 'padrao') {
        filtered = filtered.filter(perfil => perfil.is_padrao);
      } else if (statusFilter === 'orfao') {
        filtered = filtered.filter(perfil => perfil.total_usuarios === 0);
      }
    }

    setFilteredPerfis(filtered);
  }, [searchText, perfis, statusFilter]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para abrir modal de detalhes
  const handleAbrirDetalhes = (perfil: PerfilPermissao) => {
    setPerfilDetalhes(perfil);
    modalUtils.abrirModal('modal_detalhes_perfil');
  };

  // Função para abrir modal de edição
  const handleAbrirEdicao = (perfil: PerfilPermissao) => {
    setPerfilEditor(perfil);
    setIsCreatingNew(false);
    modalUtils.abrirModal('modal_editor_perfil');
  };

  // Função para abrir modal de criação
  const handleAbrirCriacao = () => {
    setPerfilEditor(null);
    setIsCreatingNew(true);
    modalUtils.abrirModal('modal_editor_perfil');
  };

  /* Função para duplicar perfil
  const handleDuplicarPerfil = async (perfil: PerfilPermissao) => {
    try {
      const response = await apiService.duplicatePerfilPermissao({
        id_perfil_origem: perfil.id,
        novo_nome: `${perfil.nome} - Cópia`,
        novo_codigo: `${perfil.codigo}_COPY`,
        nova_descricao: `Cópia do perfil: ${perfil.descricao || perfil.nome}`
      });
      
      if (response.success) {
        await fetchPerfis();
        // Mostrar mensagem de sucesso (implementar toast/notification)
      }
    } catch (error) {
      console.error('Erro ao duplicar perfil:', error);
      // Mostrar mensagem de erro
    }
  };*/

  // Função para obter badge do status
  const getStatusBadge = (ativo: boolean, isPadrao: boolean) => {
    if (isPadrao) {
      return <span className="badge bg-info">🛡️ Padrão</span>;
    }
    return ativo ? (
      <span className="badge badge-pill badge-status bg-success">Ativo</span>
    ) : (
      <span className="badge badge-pill badge-status bg-danger">Inativo</span>
    );
  };

  // Função para obter badge de usuários
  const getUsuariosBadge = (totalUsuarios: number) => {
    if (totalUsuarios === 0) {
      return <span className="badge bg-warning text-dark">🚫 Órfão</span>;
    }
    
    let badgeClass = 'bg-primary';
    if (totalUsuarios >= 10) badgeClass = 'bg-success';
    else if (totalUsuarios >= 5) badgeClass = 'bg-info';
    
    return <span className={`badge ${badgeClass}`}>{totalUsuarios} usuários</span>;
  };

  const columns: TableColumn[] = [
    {
      title: "Perfil",
      dataIndex: "nome",
      key: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <div 
            className="avatar avatar-sm me-2 rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
            style={{ 
              backgroundColor: record.cor || '#6B7280',
              width: '32px',
              height: '32px'
            }}
          >
            {record.nome.charAt(0).toUpperCase()}
          </div>
          <div>
            <h6 className="mb-0 d-flex align-items-center">
              {record.nome}
              {record.is_padrao && <i className="ti ti-shield-check text-info ms-1" title="Perfil padrão do sistema"></i>}
            </h6>
            <small className="text-muted">{record.codigo}</small>
          </div>
        </div>
      ),
    },
    {
      title: "Descrição",
      dataIndex: "descricao",
      key: "descricao",
      render: (descricao) => descricao || <span className="text-muted">-</span>,
    },
    {
      title: "Usuários",
      dataIndex: "total_usuarios",
      key: "usuarios",
      render: (_, record) => getUsuariosBadge(record.total_usuarios || 0),
      sorter: (a, b) => (a.total_usuarios || 0) - (b.total_usuarios || 0),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (ativo, record) => getStatusBadge(ativo, record.is_padrao),
      sorter: (a, b) => {
        const aValue = a.ativo ? 1 : 0;
        const bValue = b.ativo ? 1 : 0;
        return bValue - aValue; // Ativos primeiro
      },
    },
    {
      title: "Criado em",
      dataIndex: "data_criacao",
      key: "data_criacao",
      render: (data_criacao) => {
        if (!data_criacao) return <span className="text-muted">-</span>;
        
        const date = new Date(data_criacao);
        return <span className="text-muted">{date.toLocaleDateString('pt-BR')}</span>;
      },
      sorter: (a, b) => {
        const aDate = a.data_criacao ? new Date(a.data_criacao).getTime() : 0;
        const bDate = b.data_criacao ? new Date(b.data_criacao).getTime() : 0;
        return bDate - aDate; // Mais recente primeiro
      },
    },
    {
      title: "Ações",
      key: "acoes",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Link
            to="#"
            className="btn btn-sm btn-outline-info"
            onClick={() => handleAbrirDetalhes(record)}
            title="Ver Detalhes"
          >
            <i className="ti ti-eye"></i>
          </Link>
          <Link
            to="#"
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleAbrirEdicao(record)}
            title="Editar Perfil"
          >
            <i className="ti ti-edit"></i>
          </Link>
          <Link
            to="#"
            className="btn btn-sm btn-outline-success"
            onClick={() => {/* TODO: Implementar duplicação */}}
            title="Duplicar Perfil"
          >
            <i className="ti ti-copy"></i>
          </Link>
          {!record.is_padrao && (
            <Link
              to="#"
              className="btn btn-sm btn-outline-danger"
              onClick={() => {/* Implementar exclusão */}}
              title="Excluir Perfil"
            >
              <i className="ti ti-trash"></i>
            </Link>
          )}
        </div>
      ),
    }
  ];

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Contadores para estatísticas
  const totalPerfis = perfis.length;
  const perfisAtivos = perfis.filter(p => p.ativo).length;
  const perfisOrfaos = perfis.filter(p => (p.total_usuarios || 0) === 0).length;
  const perfisPadrao = perfis.filter(p => p.is_padrao).length;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      🎭 Perfis de Acesso<span className="count-title">{filteredPerfis.length} perfis</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards de Estatísticas */}
              <div className="row mb-4">
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-primary-light rounded me-3">
                          <i className="ti ti-shield-check fs-24 text-primary"></i>
                        </div>
                        <div>
                          <span className="text-muted">Total de Perfis</span>
                          <h4 className="mb-0">{totalPerfis}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-success-light rounded me-3">
                          <i className="ti ti-check-circle fs-24 text-success"></i>
                        </div>
                        <div>
                          <span className="text-muted">Perfis Ativos</span>
                          <h4 className="mb-0">{perfisAtivos}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-warning-light rounded me-3">
                          <i className="ti ti-alert-triangle fs-24 text-warning"></i>
                        </div>
                        <div>
                          <span className="text-muted">Perfis Órfãos</span>
                          <h4 className="mb-0">{perfisOrfaos}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-info-light rounded me-3">
                          <i className="ti ti-star fs-24 text-info"></i>
                        </div>
                        <div>
                          <span className="text-muted">Perfis Padrão</span>
                          <h4 className="mb-0">{perfisPadrao}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            <i className="ti ti-search" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Perfil"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="d-flex align-items-center">
                        <label className="me-2 text-muted">Filtrar:</label>
                        <select 
                          className="form-select form-select-sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">Todos os perfis</option>
                          <option value="ativo">✅ Ativos</option>
                          <option value="inativo">❌ Inativos</option>
                          <option value="padrao">🛡️ Padrão</option>
                          <option value="orfao">⚠️ Órfãos</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-sm-4 text-end">
                      <button
                        className="btn btn-primary"
                        onClick={handleAbrirCriacao}
                      >
                        <i className="ti-plus me-1"></i>
                        Novo Perfil
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Mais Usuários
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Mais Recente
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Alerta para perfis órfãos */}
                    {perfisOrfaos > 0 && (
                      <div className="alert alert-warning mb-0 py-2 px-3">
                        <i className="ti ti-alert-triangle me-1"></i>
                        <strong>{perfisOrfaos}</strong> perfil(s) sem usuários associados
                      </div>
                    )}
                  </div>

                  {/* Tabela de Perfis */}
                  <div className="table-responsive custom-table">
                    {searchText && filteredPerfis.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum perfil encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table
                        dataSource={filteredPerfis.map(perfil => ({
                          ...perfil,
                          key: perfil.id.toString()
                        }))}
                        columns={columns}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Detalhes do Perfil */}
      {perfilDetalhes && (
        <ModalDetalhesPerfil
          perfil={perfilDetalhes}
          onClose={() => {
            setPerfilDetalhes(null);
            modalUtils.fecharModal('modal_detalhes_perfil');
          }}
          onRefresh={fetchPerfis}
        />
      )}

      {/* Modal Editor de Perfil */}
      {(perfilEditor || isCreatingNew) && (
        <ModalEditorPerfil
          perfil={perfilEditor}
          isCreating={isCreatingNew}
          modulos={modulos}
          acoes={acoes}
          onClose={() => {
            setPerfilEditor(null);
            setIsCreatingNew(false);
            modalUtils.fecharModal('modal_editor_perfil');
          }}
          onRefresh={fetchPerfis}
        />
      )}
    </>
  );
};